/**
 * Whose desk a checklist item sits on, as the case file shows it.
 *
 * Owner ruling (Scott, review bz2bb6, 2026-10-04) added 'colleague': every
 * explorer run found items a teammate owed (Jake's retain read, Emily's
 * order check) tagged "us", which made the case look like Jess's to move.
 * The button cycles us -> colleague -> customer -> us.
 */
import type { CaseTaskOwner } from '../../services/qcpInvestigations';

/** Said as whose move it is, like the add row's "on us / on a colleague /
 *  on customer" (UX round 2026-10-13: a bare "us" beside "QC lab (Jake)"
 *  read as a label nobody understood). */
export const TASK_OWNER_LABEL: Record<CaseTaskOwner, string> = {
  us: '🔧 on us',
  colleague: '👥 on a colleague',
  customer: '⏳ on the customer',
};

const ORDER: CaseTaskOwner[] = ['us', 'colleague', 'customer'];

/** The desk one more click hands it to. */
export function nextTaskOwner(owner: CaseTaskOwner): CaseTaskOwner {
  const at = ORDER.indexOf(owner);
  return ORDER[(at + 1) % ORDER.length];
}

/** What that click will do, for the button's tooltip. */
export function taskOwnerHint(owner: CaseTaskOwner): string {
  const next = nextTaskOwner(owner);
  const where: Record<CaseTaskOwner, string> = {
    us: 'our desk',
    colleague: "a colleague's desk",
    customer: "the customer's desk (reached through the sales rep)",
  };
  return `On ${where[owner]} — click to hand it to ${where[next]}`;
}

/** "hand … to a colleague" — the sentence the undo/confirm line uses. */
export function taskOwnerAction(label: string, next: CaseTaskOwner): string {
  if (next === 'us') return `take ${label} onto our desk`;
  if (next === 'colleague') return `hand ${label} to a colleague`;
  return `hand ${label} to the customer`;
}
